import { memo, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import ChainAvatar from '@Components/ConnectedChainCard/chainAvatar';
import { CredentialsForm } from '@Components/CredentialsForm';
import { CredentialEntry } from '../credentials.types';

interface CredentialModalProps {
  entry: CredentialEntry;
  onClose: () => void;
  onSuccess: () => void;
}

const CredentialModal = memo(({ entry: { card, raw }, onClose, onSuccess }: CredentialModalProps) => {
  const { t } = useTranslation();
  const mode: 'create' | 'update' = raw.credential.createdAt ? 'update' : 'create';

  const managedFields = new Set(raw.credential.managedFields ?? []);
  const visibleFields = raw.fields.filter((f) => !managedFields.has(f.storageKey));

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/50">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="credential-modal-title"
        className="w-full max-w-[480px] overflow-hidden rounded-xl bg-white shadow-[0_24px_48px_rgba(16,24,40,.24)]">
        <div className="flex items-center gap-3 border-b border-gray-100 px-6 py-5">
          <ChainAvatar {...card.avatar} size="md" />
          <div className="flex-1 min-w-0">
            <div id="credential-modal-title" className="text-base font-semibold text-gray-900">
              {mode === 'create' ? t('Credentials.Card.Connect') : t('Credentials.Card.Edit')}
            </div>
            <div className="mt-0.5 truncate text-xs text-gray-500">
              {card.name} · {card.platform}
            </div>
          </div>
          <button
            type="button"
            aria-label={t('Global.Close')}
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6">
          <CredentialsForm
            connectorName={raw.name}
            connectorType={raw.credential.connectorType}
            fields={visibleFields}
            existingCredential={raw.credential}
            mode={mode}
            onCancel={onClose}
            onSuccess={onSuccess}
          />
        </div>
      </div>
    </div>
  );
});

CredentialModal.displayName = 'CredentialModal';
export default CredentialModal;
